import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { PROVIDERS, OAUTH_REDIRECT_URI } from '../../contexts/oauthConfig';

const GOOGLE_GSI_SRC = 'https://accounts.google.com/gsi/client';

// Load an external script once (de-duplicated across mounts).
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

function randomState() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Build a provider authorize URL and redirect the browser to it.
function startRedirectFlow(providerKey, cfg) {
  const state = randomState();
  sessionStorage.setItem('oauth_provider', providerKey);
  sessionStorage.setItem('oauth_state', state);

  const params = new URLSearchParams({
    client_id: cfg.clientId,
    redirect_uri: OAUTH_REDIRECT_URI,
    response_type: cfg.responseType,
    scope: cfg.scope,
    state,
    ...(cfg.extraParams || {}),
  });
  // OIDC implicit (id_token) requires a nonce.
  if (cfg.responseType && cfg.responseType.includes('id_token')) {
    params.set('nonce', randomState());
  }
  window.location.href = `${cfg.authorizeUrl}?${params.toString()}`;
}

/**
 * Renders a "Continue with <Provider>" button for each provider that is both
 * configured on the backend (from GET /auth/providers) and has a public client
 * id available on the frontend.
 */
const SocialLoginButtons = () => {
  const { loginWithProvider } = useAuth();
  const [enabled, setEnabled] = useState([]);
  const googleBtnRef = useRef(null);

  // Ask the backend which providers are configured (have server credentials).
  useEffect(() => {
    let cancelled = false;
    import('axios').then(({ default: axios }) => {
      axios
        .get('/auth/providers')
        .then((res) => {
          if (!cancelled) setEnabled(res.data.providers || []);
        })
        .catch(() => {
          if (!cancelled) setEnabled([]);
        });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Render the official Google button via Google Identity Services.
  useEffect(() => {
    const cfg = PROVIDERS.google;
    if (!enabled.includes('google') || !cfg.clientId || !googleBtnRef.current) return;

    let cancelled = false;
    loadScript(GOOGLE_GSI_SRC)
      .then(() => {
        if (cancelled || !window.google || !googleBtnRef.current) return;
        window.google.accounts.id.initialize({
          client_id: cfg.clientId,
          callback: (resp) => {
            // resp.credential is the Google ID token (JWT).
            loginWithProvider('google', { credential: resp.credential });
          },
        });
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          width: 320,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enabled, loginWithProvider]);

  // Non-Google providers that are enabled on the backend.
  const redirectProviders = enabled.filter(
    (key) => key !== 'google' && PROVIDERS[key] && PROVIDERS[key].clientId
  );

  if (enabled.length === 0) return null;

  return (
    <div className="mt-6">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-gray-50 text-gray-500">Or continue with</span>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {/* Google renders its own branded button here */}
        {enabled.includes('google') && PROVIDERS.google.clientId && (
          <div className="flex justify-center" ref={googleBtnRef} />
        )}

        {redirectProviders.map((key) => {
          const cfg = PROVIDERS[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => startRedirectFlow(key, cfg)}
              className={`w-full flex justify-center items-center py-2 px-4 border rounded-md text-sm font-medium transition-colors ${cfg.brandClass}`}
            >
              Continue with {cfg.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SocialLoginButtons;
